import apiClient from "./api-client.service";
import { InterviewSession, Question } from "../Interface/Index";
import { InterviewSchedule } from "../Entities/interview-schedule.entity";

export default class InterviewService {
  private static instance: InterviewService;
  private APIBaseUrl: string = "";

  constructor() {
    if (InterviewService.instance) {
      return InterviewService.instance;
    }
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  async answerSingleQuestion(
    answer: string,
    sessionId: string,
    questionId: string
  ) {
    const formData = new FormData();
    formData.append("answer", answer);
    const response = await apiClient.put(
      `${this.APIBaseUrl}/interview/${sessionId}/questions/${questionId}/response`,
      formData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const data = response.data;
    return data;
  }

  async getAvailableInterviewSlots(jobPostId: string) {
    const params = {
      jobPostId: jobPostId,
    };
    const queryString = new URLSearchParams(params).toString();
    const url = `${this.APIBaseUrl}/interview/free/slots?${queryString}`;
    const response = await apiClient(url);
    const data = response.data.map((slot: any) => {
      const localStart = new Date(slot.start).toLocaleString("en-US");
      const localEnd = new Date(slot.end).toLocaleString("en-US");
      slot.start = localStart;
      slot.end = localEnd;
      return slot;
    });
    return data;
  }

  async bookInterviewSlot(
    applicationId: string,
    startTime: string,
    endTime: string
  ) {
    const startISO = new Date(startTime).toISOString();
    const endISO = new Date(endTime).toISOString();

    const payload = {
      job_application_id: applicationId,
      start: startISO,
      end: endISO,
    };

    const response = await apiClient.post(
      `${this.APIBaseUrl}/interview/schedule`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const data = response.data;
    return data;
  }

  async getAllInterviewSessionById(
    jobApplicationId: string
  ): Promise<InterviewSession[] | undefined> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview?job_application_id=${jobApplicationId}`
      );
      const data = response.data;
      if (data.length > 0) {
        return data;
      }
    } catch (error) {
      console.error("Error fetching interview sessions:", error);
    }
  }

  async getQuestionsBySessionId(sessionId: string): Promise<Question[]> {
    const response = await apiClient.get(
      `${this.APIBaseUrl}/interview/${sessionId}/questions`
    );
    const data = response.data;
    return data;
  }

  async getOnlineInterviewDetails(
    applicationId: string
  ): Promise<InterviewSchedule[]> {
    const response = await apiClient.get<InterviewSchedule[]>(
      `${this.APIBaseUrl}/interview/schedule/${applicationId}`
    );
    const data = response.data;
    return data;
  }

  async checkForMeetingLink(applicationId: string): Promise<boolean> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview/check/link/${applicationId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ checkForMeetingLink ~ error:",
        error
      );
      return false;
    }
  }

  async findSessionById(sessionId: string): Promise<InterviewSession | null> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/interview/${sessionId}`
      );
      return response.data;
    } catch (error: any) {
      console.error("🚀 ~ InterviewService ~ findSessionById ~ error:", error);
      return null;
    }
  }


  async triggerGenerateQuestions(sessionId: string) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/interview/trigger/generate-questions`,
        { sessionId }
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ InterviewService ~ retryN8NWorkflow ~ error:", error);
      return null;
    }
  }

  async storeScreenRecording(sessionId: string, recordingFile: Blob) {
    try {
      const fileSizeInMB = (recordingFile.size / (1024 * 1024)).toFixed(2);
      console.log(
        `🚀 ~ InterviewService ~ storeScreenRecording ~ File size: ${fileSizeInMB} MB`
      );

      const formData = new FormData();
      formData.append("recordingFile", recordingFile);
      formData.append("sessionId", sessionId);
      const response = await apiClient.post(
        `${this.APIBaseUrl}/interview/store/screen-recording`,
        formData
      );
      return response.data;
    } catch (error: any) {
      const fileSizeInMB = (recordingFile.size / (1024 * 1024)).toFixed(2);
      console.error("🚀 ~ InterviewService ~ storeScreenRecording ~ error:", {
        message: error?.message || "Unknown error",
        response: error?.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            }
          : null,
        request: error?.request || null,
        config: error?.config
          ? {
              url: error.config.url,
              method: error.config.method,
              headers: error.config.headers,
            }
          : null,
        recordingFileSize: `${fileSizeInMB} MB`,
        sessionId: sessionId,
        fullError: error,
      });
      return null;
    }
  }
}
